import React, { useMemo, useState } from "react";
import { CalendarDays, Download, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { supabase } from "../../../utils/supabase";

const PAGE_SIZE = 1000;
const FULL_BACKUP_START_DATE = "2026-01-23";

const EXPORTS = [
  {
    key: "offers",
    table: "offers",
    label: "Ofertas",
    description: "Cabecera de ofertas comerciales, estado, importes y condiciones.",
    sheetName: "Ofertas",
    orderBy: "created_at",
  },
  {
    key: "clients",
    table: "clients",
    label: "Clientes",
    description: "Razones sociales, CUIT, contactos, direcciones y datos de facturacion.",
    sheetName: "Clientes",
    orderBy: "created_at",
  },
  {
    key: "instruments",
    table: "instruments",
    label: "Instrumentos",
    description: "Instrumentos registrados, trazabilidad, fechas y datos tecnicos.",
    sheetName: "Instrumentos",
    orderBy: "created_at",
  },
];

const WEEKLY_ADMIN_SHEETS = [
  { key: "summary", label: "Resumen semanal", sheetName: "Resumen semanal" },
  { key: "movements", label: "Movimientos", sheetName: "Movimientos" },
  { key: "clientsAndOffers", label: "Clientes y ofertas", sheetName: "Clientes y ofertas" },
  { key: "instrumentsAndOrders", label: "Instrumentos y OS", sheetName: "Instrumentos y OS" },
  { key: "remitosAndBilling", label: "Remitos y facturacion", sheetName: "Remitos y factura" },
];

const WEEKLY_RAW_SHEETS = [
  { key: "offers", label: "Datos ofertas", sheetName: "Datos - ofertas" },
  { key: "offerServices", label: "Datos items oferta", sheetName: "Datos - items oferta" },
  { key: "offerAudit", label: "Datos historial ofertas", sheetName: "Datos - hist ofertas" },
  { key: "clients", label: "Datos clientes", sheetName: "Datos - clientes" },
  { key: "purchaseOrderView", label: "Datos OC", sheetName: "Datos - OC" },
  { key: "intakes", label: "Datos ingresos", sheetName: "Datos - ingresos" },
  { key: "instruments", label: "Datos instrumentos", sheetName: "Datos - instrumentos" },
  { key: "serviceOrdersEnriched", label: "Datos OS", sheetName: "Datos - OS" },
  { key: "remitoSearch", label: "Datos remitos", sheetName: "Datos - remitos" },
  { key: "billingView", label: "Datos facturacion", sheetName: "Datos - factura" },
];

const FULL_BACKUP_SHEETS = [
  {
    key: "clients",
    table: "clients",
    label: "Clientes",
    sheetName: "Clientes",
    description: "Maestro de clientes, contactos, CUIT y datos administrativos.",
  },
  {
    key: "offers",
    table: "offers",
    label: "Ofertas",
    sheetName: "Ofertas",
    description: "Cabeceras de ofertas comerciales y estados.",
  },
  {
    key: "offerServices",
    table: "offer_services",
    label: "Items de ofertas",
    sheetName: "Items oferta",
    description: "Servicios, cantidades e importes incluidos en ofertas.",
  },
  {
    key: "offerAudit",
    table: "offer_audit",
    label: "Historial de ofertas",
    sheetName: "Hist ofertas",
    description: "Cambios y trazabilidad de ofertas.",
  },
  {
    key: "offerServicesAudit",
    table: "offer_services_audit",
    label: "Historial items oferta",
    sheetName: "Hist items oferta",
    description: "Cambios y trazabilidad de items de oferta.",
  },
  {
    key: "purchaseOrders",
    table: "purchase_orders",
    label: "Ordenes de compra",
    sheetName: "OC",
    description: "OC cargadas y vinculadas a clientes/ofertas.",
  },
  {
    key: "purchaseOrderView",
    table: "v_purchase_orders",
    label: "OC enriquecidas",
    sheetName: "OC vista",
    description: "Vista de OC con datos preparados para consulta administrativa.",
  },
  {
    key: "intakes",
    table: "intakes",
    label: "Ingresos",
    sheetName: "Ingresos",
    description: "Ingresos provisorios o recepciones registradas.",
  },
  {
    key: "intakeItems",
    table: "intake_items",
    label: "Items de ingresos",
    sheetName: "Items ingresos",
    description: "Instrumentos asociados a ingresos.",
  },
  {
    key: "instruments",
    table: "instruments",
    label: "Instrumentos",
    sheetName: "Instrumentos",
    description: "Maestro y trazabilidad de instrumentos.",
  },
  {
    key: "serviceOrders",
    table: "service_orders",
    label: "Ordenes de servicio",
    sheetName: "OS",
    description: "Trabajos operativos, estados y tecnicos asignados.",
  },
  {
    key: "serviceOrdersEnriched",
    table: "v_service_orders_enriched",
    label: "OS enriquecidas",
    sheetName: "OS vista",
    description: "Vista de OS con datos de cliente, OC e instrumento.",
  },
  {
    key: "servicePrices",
    table: "service_prices",
    label: "Precios de OS",
    sheetName: "Precios OS",
    description: "Precios principales asociados a ordenes de servicio.",
  },
  {
    key: "servicePriceExtras",
    table: "service_price_extras",
    label: "Extras de precios",
    sheetName: "Extras precios",
    description: "Adicionales o extras vinculados a precios de OS.",
  },
  {
    key: "technicians",
    table: "technicians",
    label: "Tecnicos",
    sheetName: "Tecnicos",
    description: "Maestro de tecnicos y usuarios operativos.",
  },
  {
    key: "remitos",
    table: "remitos",
    label: "Remitos",
    sheetName: "Remitos",
    description: "Cabeceras de remitos generados o emitidos.",
  },
  {
    key: "remitoItems",
    table: "remito_items",
    label: "Items de remitos",
    sheetName: "Items remitos",
    description: "Detalle de instrumentos y OS incluidos en remitos.",
  },
  {
    key: "remitoSearch",
    table: "v_remitos_search",
    label: "Remitos enriquecidos",
    sheetName: "Remitos vista",
    description: "Vista de remitos para busqueda y control.",
  },
  {
    key: "billingInvoices",
    table: "billing_invoices",
    label: "Facturas",
    sheetName: "Facturas",
    description: "Solicitudes y emisiones de factura.",
  },
  {
    key: "billingInvoiceItems",
    table: "billing_invoice_items",
    label: "Items de facturas",
    sheetName: "Items facturas",
    description: "Detalle de conceptos facturados.",
  },
  {
    key: "billingView",
    table: "v_billing_invoices_list",
    label: "Facturas enriquecidas",
    sheetName: "Facturas vista",
    description: "Vista administrativa de facturacion.",
  },
];

function normalizeCell(value) {
  if (value == null) return "";
  if (value instanceof Date) return value;
  if (Array.isArray(value) || typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return value;
}

function normalizeRows(rows = []) {
  return rows.map((row) =>
    Object.fromEntries(
      Object.entries(row || {}).map(([key, value]) => [key, normalizeCell(value)])
    )
  );
}

function timestampForFile() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
  ].join("-");
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(dateInput, days) {
  const date = typeof dateInput === "string" ? new Date(`${dateInput}T12:00:00`) : new Date(dateInput);
  date.setDate(date.getDate() + days);
  return date;
}

function getPreviousWeekRange() {
  const today = new Date();
  const day = today.getDay();
  const daysSinceMonday = (day + 6) % 7;
  const thisMonday = addDays(today, -daysSinceMonday);
  const previousMonday = addDays(thisMonday, -7);
  const previousSunday = addDays(previousMonday, 6);
  return {
    startDate: toDateInputValue(previousMonday),
    endDate: toDateInputValue(previousSunday),
  };
}

function getEndExclusive(endDate) {
  return toDateInputValue(addDays(endDate, 1));
}

function formatDate(value) {
  if (!value) return "";
  const raw = String(value);
  const dateOnly = raw.includes("T") ? raw.slice(0, 10) : raw.slice(0, 10);
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isWithinRange(value, startDate, endExclusive) {
  if (!value) return false;
  const valueDate = String(value).slice(0, 10);
  return valueDate >= startDate && valueDate < endExclusive;
}

function uniqueRows(rows = [], idField = "id") {
  const map = new Map();
  for (const row of rows || []) {
    const key = row?.[idField] ?? JSON.stringify(row || {});
    if (!map.has(key)) map.set(key, row);
  }
  return Array.from(map.values());
}

function valuesFrom(rows = [], field) {
  return Array.from(
    new Set((rows || []).map((row) => row?.[field]).filter(Boolean))
  );
}

function mapById(rows = [], idField = "id") {
  const map = new Map();
  for (const row of rows || []) {
    const id = row?.[idField];
    if (id && !map.has(id)) map.set(id, row);
  }
  return map;
}

function groupBy(rows = [], field) {
  const map = new Map();
  for (const row of rows || []) {
    const value = row?.[field];
    if (!value) continue;
    if (!map.has(value)) map.set(value, []);
    map.get(value).push(row);
  }
  return map;
}

function countBy(rows = [], field) {
  const counts = {};
  for (const row of rows || []) {
    const value = row?.[field] || "Sin dato";
    counts[value] = (counts[value] || 0) + 1;
  }
  return counts;
}

function firstNonEmpty(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";
}

function joinUnique(values = []) {
  return Array.from(new Set(values.filter(Boolean).map((value) => String(value)))).join(", ");
}

function clientCode(client) {
  if (!client?.client_number) return "";
  return client?.branch_no ? `${client.client_number} ${client.branch_no}` : String(client.client_number);
}

function statusRows(counts = {}, labels = {}) {
  const entries = Object.entries(counts);
  if (!entries.length) return [["Sin registros", 0, "No hubo movimientos visibles para este bloque"]];
  return entries
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([status, count]) => [status, count, labels[status] || "Revisar detalle en la hoja correspondiente"]);
}

function countStatuses(counts = {}, statuses = []) {
  return statuses.reduce((total, status) => total + (Number(counts[status]) || 0), 0);
}

function makeActionRows({ offerStatus, osStatus, remitoStatus, invoiceStatus }) {
  const rows = [
    [
      "Ofertas por cerrar",
      countStatuses(offerStatus, ["sent", "accepted", "billing"]),
      "Revisar aceptacion, OC asociada y si corresponde pedir facturacion",
    ],
    [
      "OS listas para remitar",
      countStatuses(osStatus, ["certificates_finished_controlled", "uploaded_for_remito"]),
      "Controlar certificados y pasar a remito si ya esta completo",
    ],
    [
      "OS con remito generado",
      countStatuses(osStatus, ["remito_generated"]),
      "Verificar que el remito este emitido y disponible para el cliente",
    ],
    [
      "Remitos en borrador",
      countStatuses(remitoStatus, ["borrador", "draft"]),
      "Confirmar si falta emitir, corregir o anular",
    ],
    [
      "Facturas pendientes",
      countStatuses(invoiceStatus, ["requested", "pending", "solicitada", "pendiente"]),
      "Revisar solicitudes de factura y datos administrativos",
    ],
  ];

  return rows.filter(([, count]) => count > 0);
}

function shouldIgnoreOptionalDateField(error) {
  const msg = String(error?.message || "").toLowerCase();
  return (
    msg.includes("could not find") ||
    msg.includes("does not exist") ||
    (msg.includes("column") && msg.includes("schema cache"))
  );
}

function autosizeColumns(worksheet, rows) {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  worksheet["!cols"] = headers.map((header) => {
    const maxContent = rows.reduce((max, row) => {
      const value = row?.[header];
      return Math.max(max, String(value ?? "").length);
    }, header.length);
    return { wch: Math.min(Math.max(maxContent + 2, 12), 45) };
  });
}

function addAutoFilter(worksheet) {
  if (!worksheet?.["!ref"]) return;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: range.s.r, c: range.s.c },
      e: { r: range.s.r, c: range.e.c },
    }),
  };
}

async function fetchAllRows({ table, orderBy }) {
  const rows = [];
  let from = 0;

  for (;;) {
    let query = supabase
      .from(table)
      .select("*")
      .range(from, from + PAGE_SIZE - 1);

    if (orderBy) {
      query = query.order(orderBy, { ascending: false });
    }

    const { data, error } = await query;
    if (error) throw error;

    const chunk = data || [];
    rows.push(...chunk);

    if (chunk.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

async function fetchBackupDataset(item) {
  try {
    const rows = await fetchAllRows({ table: item.table, orderBy: item.orderBy });
    return { item, rows, error: null };
  } catch (error) {
    console.warn(`[AdminExports] No se pudo incluir ${item.table} en el backup`, error);
    return {
      item,
      rows: [
        {
          tabla: item.table,
          aviso: "No se pudo descargar esta tabla o vista",
          detalle: error?.message || "Error desconocido",
        },
      ],
      error,
    };
  }
}

async function fetchRowsWithQuery({ table, applyFilters, orderBy }) {
  const rows = [];
  let from = 0;

  for (;;) {
    let query = supabase.from(table).select("*");
    if (typeof applyFilters === "function") {
      query = applyFilters(query);
    }
    if (orderBy) {
      query = query.order(orderBy, { ascending: false });
    }
    query = query.range(from, from + PAGE_SIZE - 1);

    const { data, error } = await query;
    if (error) throw error;

    const chunk = data || [];
    rows.push(...chunk);

    if (chunk.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

async function fetchRowsByDateFields({ table, dateFields, startDate, endExclusive }) {
  const chunks = [];

  for (const field of dateFields || []) {
    try {
      const rows = await fetchRowsWithQuery({
        table,
        orderBy: field,
        applyFilters: (query) => query.gte(field, startDate).lt(field, endExclusive),
      });
      chunks.push(...rows);
    } catch (error) {
      if (!shouldIgnoreOptionalDateField(error)) throw error;
      console.warn(`[AdminExports] ${table}.${field} no disponible para filtro`, error);
    }
  }

  return uniqueRows(chunks);
}

async function fetchRowsByIds({ table, column, ids, orderBy = null }) {
  const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
  if (!uniqueIds.length) return [];

  const rows = [];
  for (let i = 0; i < uniqueIds.length; i += 200) {
    const chunk = uniqueIds.slice(i, i + 200);
    const data = await fetchRowsWithQuery({
      table,
      orderBy,
      applyFilters: (query) => query.in(column, chunk),
    });
    rows.push(...data);
  }

  return uniqueRows(rows);
}

function appendSheet(workbook, exportConfig, rows, { hidden = false } = {}) {
  const normalized = normalizeRows(
    rows?.length ? rows : [{ info: "Sin registros para el periodo seleccionado" }]
  );
  const worksheet = XLSX.utils.json_to_sheet(normalized);
  autosizeColumns(worksheet, normalized);
  addAutoFilter(worksheet);
  XLSX.utils.book_append_sheet(workbook, worksheet, exportConfig.sheetName);
  if (hidden) {
    const index = workbook.SheetNames.length - 1;
    workbook.Workbook = workbook.Workbook || {};
    workbook.Workbook.Sheets = workbook.Workbook.Sheets || [];
    workbook.Workbook.Sheets[index] = {
      ...(workbook.Workbook.Sheets[index] || {}),
      name: exportConfig.sheetName,
      Hidden: 1,
    };
  }
}

function safeTableName(name, index = 0) {
  return `Tabla_${String(name || "datos")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/^([^A-Za-z_])/, "_$1")
    .slice(0, 40)}_${index}`;
}

function styleCell(cell, options = {}) {
  if (options.font) cell.font = options.font;
  if (options.fill) cell.fill = options.fill;
  if (options.alignment) cell.alignment = options.alignment;
  if (options.border) cell.border = options.border;
}

const EXCEL_COLORS = {
  navy: "0F4C81",
  blue: "1F77B4",
  lightBlue: "D9EAF7",
  green: "70AD47",
  lightGreen: "E2F0D9",
  amber: "F4B183",
  lightAmber: "FCE4D6",
  gray: "F3F6FA",
  darkGray: "44546A",
  white: "FFFFFF",
  border: "D9E2F3",
};

const thinBorder = {
  top: { style: "thin", color: { argb: EXCEL_COLORS.border } },
  left: { style: "thin", color: { argb: EXCEL_COLORS.border } },
  bottom: { style: "thin", color: { argb: EXCEL_COLORS.border } },
  right: { style: "thin", color: { argb: EXCEL_COLORS.border } },
};

function addSummaryBlock(worksheet, title, headers, rows, startRow, color = EXCEL_COLORS.navy) {
  worksheet.mergeCells(startRow, 1, startRow, 3);
  const titleCell = worksheet.getCell(startRow, 1);
  styleCell(titleCell, {
    font: { bold: true, color: { argb: EXCEL_COLORS.white }, size: 12 },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: color } },
    alignment: { vertical: "middle" },
  });
  titleCell.value = title;
  worksheet.getRow(startRow).height = 22;

  const headerRow = worksheet.getRow(startRow + 1);
  headerRow.values = ["", ...headers];
  headerRow.eachCell((cell) =>
    styleCell(cell, {
      font: { bold: true, color: { argb: EXCEL_COLORS.darkGray } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.gray } },
      border: thinBorder,
      alignment: { vertical: "middle", wrapText: true },
    })
  );

  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(startRow + 2 + index);
    excelRow.values = ["", ...row];
    excelRow.eachCell((cell, colNumber) =>
      styleCell(cell, {
        border: thinBorder,
        alignment: {
          vertical: "top",
          horizontal: colNumber === 2 ? "center" : "left",
          wrapText: true,
        },
      })
    );
    if (index % 2 === 1) {
      excelRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FAFBFC" } };
      });
    }
  });

  return startRow + rows.length + 3;
}

function appendStyledWeeklySummarySheet(
  workbook,
  rows,
  { title: titleText = "Reporte semanal de movimientos", sheetName = "Resumen semanal" } = {}
) {
  const actionRows = rows.actionItems?.length
    ? rows.actionItems
    : [["Sin pendientes detectados", 0, "No se detectaron alertas administrativas por estado"]];
  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 6 }],
    properties: { defaultRowHeight: 18 },
  });

  worksheet.columns = [
    { width: 3 },
    { width: 36 },
    { width: 16 },
    { width: 82 },
  ];

  worksheet.mergeCells("A1:D1");
  const title = worksheet.getCell("A1");
  title.value = titleText;
  styleCell(title, {
    font: { bold: true, size: 18, color: { argb: EXCEL_COLORS.white } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.navy } },
    alignment: { horizontal: "center", vertical: "middle" },
  });
  worksheet.getRow(1).height = 30;

  worksheet.mergeCells("A2:D2");
  const subtitle = worksheet.getCell("A2");
  subtitle.value = `Periodo: ${rows?.periodLabel || ""} | Generado: ${rows?.generatedAt || ""}`;
  styleCell(subtitle, {
    font: { italic: true, color: { argb: EXCEL_COLORS.darkGray } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.lightBlue } },
    alignment: { horizontal: "center" },
  });

  let cursor = 4;
  cursor = addSummaryBlock(
    worksheet,
    "Indicadores principales",
    ["Indicador", "Cantidad", "Detalle"],
    rows.kpis,
    cursor,
    EXCEL_COLORS.blue
  );
  cursor = addSummaryBlock(
    worksheet,
    "Pendientes administrativos",
    ["Tema", "Cantidad", "Accion sugerida"],
    actionRows,
    cursor,
    EXCEL_COLORS.amber
  );
  cursor = addSummaryBlock(
    worksheet,
    "Estados de ofertas",
    ["Estado", "Cantidad", "Lectura administrativa"],
    rows.offerStatus,
    cursor,
    EXCEL_COLORS.navy
  );
  cursor = addSummaryBlock(
    worksheet,
    "Estados de ordenes de servicio",
    ["Estado", "Cantidad", "Lectura administrativa"],
    rows.serviceOrderStatus,
    cursor,
    EXCEL_COLORS.green
  );
  cursor = addSummaryBlock(
    worksheet,
    "Estados de remitos",
    ["Estado", "Cantidad", "Lectura administrativa"],
    rows.remitoStatus,
    cursor,
    EXCEL_COLORS.darkGray
  );
  cursor = addSummaryBlock(
    worksheet,
    "Guia rapida",
    ["Hoja", "Para que sirve", "Uso recomendado"],
    rows.guide,
    cursor,
    EXCEL_COLORS.blue
  );
  addSummaryBlock(
    worksheet,
    "Orden sugerido de revision",
    ["Paso", "Hoja", "Que revisar"],
    rows.workflow,
    cursor,
    EXCEL_COLORS.green
  );

  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = { ...(cell.alignment || {}), wrapText: true, vertical: "top" };
    });
  });
}

function appendBackupIndexSheet(workbook, datasets) {
  const generatedAt = new Date().toLocaleString("es-AR");
  const totalRecords = datasets.reduce(
    (total, dataset) => total + (dataset.error ? 0 : dataset.rows.length),
    0
  );
  const errors = datasets.filter((dataset) => dataset.error).length;
  const worksheet = workbook.addWorksheet("Indice backup", {
    views: [{ state: "frozen", ySplit: 7 }],
    properties: { defaultRowHeight: 18 },
  });

  worksheet.columns = [
    { width: 3 },
    { width: 28 },
    { width: 30 },
    { width: 14 },
    { width: 14 },
    { width: 70 },
  ];

  worksheet.mergeCells("A1:F1");
  const title = worksheet.getCell("A1");
  title.value = "Backup completo de la base de datos";
  styleCell(title, {
    font: { bold: true, size: 18, color: { argb: EXCEL_COLORS.white } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.navy } },
    alignment: { horizontal: "center", vertical: "middle" },
  });
  worksheet.getRow(1).height = 30;

  worksheet.mergeCells("A2:F2");
  const subtitle = worksheet.getCell("A2");
  subtitle.value = `Generado: ${generatedAt} | Alcance: todos los registros visibles para este usuario`;
  styleCell(subtitle, {
    font: { italic: true, color: { argb: EXCEL_COLORS.darkGray } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.lightBlue } },
    alignment: { horizontal: "center", vertical: "middle", wrapText: true },
  });

  const kpis = [
    ["Tablas y vistas incluidas", datasets.length, "Incluye datos comerciales, operativos y administrativos"],
    ["Registros descargados", totalRecords, "Suma de filas descargadas sin contar hojas con error"],
    ["Tablas con aviso", errors, "Revisar la columna Estado si alguna vista no esta disponible"],
  ];
  addSummaryBlock(
    worksheet,
    "Resumen del backup",
    ["Indicador", "Cantidad", "Detalle"],
    kpis,
    4,
    EXCEL_COLORS.blue
  );

  const startRow = 11;
  worksheet.mergeCells(startRow, 1, startRow, 6);
  const blockTitle = worksheet.getCell(startRow, 1);
  blockTitle.value = "Contenido incluido";
  styleCell(blockTitle, {
    font: { bold: true, color: { argb: EXCEL_COLORS.white }, size: 12 },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.green } },
    alignment: { vertical: "middle" },
  });

  const headers = ["Hoja", "Tabla o vista", "Filas", "Estado", "Descripcion"];
  const headerRow = worksheet.getRow(startRow + 1);
  headerRow.values = ["", ...headers];
  headerRow.eachCell((cell) =>
    styleCell(cell, {
      font: { bold: true, color: { argb: EXCEL_COLORS.darkGray } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.gray } },
      border: thinBorder,
      alignment: { vertical: "middle", wrapText: true },
    })
  );

  datasets.forEach((dataset, index) => {
    const row = worksheet.getRow(startRow + 2 + index);
    row.values = [
      "",
      dataset.item.sheetName,
      dataset.item.table,
      dataset.error ? 0 : dataset.rows.length,
      dataset.error ? "Aviso" : "OK",
      dataset.error ? dataset.error?.message || "No disponible" : dataset.item.description,
    ];
    row.eachCell((cell, colNumber) =>
      styleCell(cell, {
        border: thinBorder,
        alignment: {
          vertical: "top",
          horizontal: colNumber === 4 ? "center" : "left",
          wrapText: true,
        },
        fill:
          dataset.error && colNumber === 5
            ? { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.lightAmber } }
            : undefined,
      })
    );
  });
}

function buildFriendlyRows(item, rows, context = {}) {
  const clientsById = mapById(context.clients || []);

  if (item.key === "clients") {
    return rows.map((client) => ({
      Cliente: firstNonEmpty(client.name, client.legal_name, client.business_name),
      "Codigo cliente": clientCode(client),
      CUIT: firstNonEmpty(client.cuit, client.tax_id),
      Email: firstNonEmpty(client.email, client.billing_email, client.contact_email),
      Telefono: firstNonEmpty(client.phone, client.telephone, client.mobile),
      Contacto: firstNonEmpty(client.contact_name, client.contact, client.responsible_name),
      Direccion: firstNonEmpty(client.address, client.billing_address),
      Localidad: firstNonEmpty(client.city, client.locality),
      Provincia: firstNonEmpty(client.province, client.state),
      "Fecha alta": formatDate(client.created_at),
      "Ultima actualizacion": formatDate(client.updated_at),
    }));
  }

  if (item.key === "offers") {
    return rows.map((offer) => {
      const client = clientsById.get(offer.client_id);
      return {
        Oferta: firstNonEmpty(offer.code, offer.base_number, offer.id),
        Revision: offer.revision ?? "",
        Cliente: firstNonEmpty(client?.name, offer.client_name, offer.client_id),
        "Codigo cliente": clientCode(client),
        CUIT: firstNonEmpty(client?.cuit, client?.tax_id),
        Estado: offer.status || "",
        "Condicion comercial": offer.commercial_condition || "",
        Moneda: offer.price_currency || "",
        Subtotal: offer.subtotal ?? "",
        Total: offer.total ?? "",
        "Fecha oferta": formatDate(firstNonEmpty(offer.offer_date, offer.created_at)),
        "Solicitud facturacion": formatDate(offer.billing_requested_date),
        "Facturada": formatDate(offer.invoiced_date),
        "Ultima actualizacion": formatDate(offer.updated_at),
      };
    });
  }

  if (item.key === "instruments") {
    return rows.map((instrument) => {
      const client = clientsById.get(instrument.client_id);
      return {
        Cliente: firstNonEmpty(client?.name, instrument.client_name, instrument.client_id),
        "Codigo cliente": clientCode(client),
        Instrumento: [instrument.type, instrument.brand, instrument.model]
          .filter(Boolean)
          .join(" "),
        "ID Lab": instrument.lab_id || "",
        Marca: instrument.brand || "",
        Modelo: instrument.model || "",
        Serie: firstNonEmpty(instrument.serial, instrument.serial_number),
        Estado: firstNonEmpty(instrument.status, instrument.state),
        "Fecha ingreso": formatDate(firstNonEmpty(instrument.reception_date, instrument.created_at)),
        "Fecha salida": formatDate(instrument.departure_date),
        Observacion: firstNonEmpty(instrument.notes, instrument.observation, instrument.description),
      };
    });
  }

  return normalizeRows(rows);
}

function appendIndividualSummarySheet(workbook, item, rawRows, friendlyRows) {
  const worksheet = workbook.addWorksheet("Resumen", {
    views: [{ state: "frozen", ySplit: 6 }],
    properties: { defaultRowHeight: 18 },
  });

  worksheet.columns = [
    { width: 3 },
    { width: 36 },
    { width: 16 },
    { width: 82 },
  ];

  worksheet.mergeCells("A1:D1");
  const title = worksheet.getCell("A1");
  title.value = `Exportacion de ${item.label}`;
  styleCell(title, {
    font: { bold: true, size: 18, color: { argb: EXCEL_COLORS.white } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.navy } },
    alignment: { horizontal: "center", vertical: "middle" },
  });
  worksheet.getRow(1).height = 30;

  worksheet.mergeCells("A2:D2");
  const subtitle = worksheet.getCell("A2");
  subtitle.value = `Generado: ${new Date().toLocaleString("es-AR")} | Alcance: todos los registros visibles para este usuario`;
  styleCell(subtitle, {
    font: { italic: true, color: { argb: EXCEL_COLORS.darkGray } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.lightBlue } },
    alignment: { horizontal: "center", vertical: "middle", wrapText: true },
  });

  let cursor = 4;
  cursor = addSummaryBlock(
    worksheet,
    "Resumen de descarga",
    ["Indicador", "Cantidad", "Detalle"],
    [
      ["Registros descargados", rawRows.length, item.description],
      ["Columnas visibles", Object.keys(friendlyRows[0] || {}).length, "Columnas simplificadas para lectura administrativa"],
      ["Hoja de respaldo", 1, "La tabla original completa queda oculta para auditoria o soporte"],
    ],
    cursor,
    EXCEL_COLORS.blue
  );

  const statusCounts = countBy(rawRows, "status");
  if (Object.keys(statusCounts).length) {
    cursor = addSummaryBlock(
      worksheet,
      "Estados detectados",
      ["Estado", "Cantidad", "Lectura administrativa"],
      statusRows(statusCounts),
      cursor,
      EXCEL_COLORS.green
    );
  }

  addSummaryBlock(
    worksheet,
    "Como usar este Excel",
    ["Hoja", "Para que sirve", "Uso recomendado"],
    [
      [item.sheetName, "Vista principal simplificada", "Usar esta hoja para consultas administrativas diarias"],
      [`Datos - ${item.sheetName}`, "Tabla original completa", "Usarla solo cuando falte una columna tecnica o para soporte"],
    ],
    cursor,
    EXCEL_COLORS.darkGray
  );
}

function appendStyledTableSheet(workbook, exportConfig, rows, { hidden = false, index = 0 } = {}) {
  const normalized = normalizeRows(
    rows?.length ? rows : [{ info: "Sin registros para el periodo seleccionado" }]
  );
  const headers = Object.keys(normalized[0] || { info: "" });
  const worksheet = workbook.addWorksheet(exportConfig.sheetName);
  worksheet.state = hidden ? "hidden" : "visible";
  worksheet.views = [{ state: "frozen", ySplit: 1 }];

  worksheet.addTable({
    name: safeTableName(exportConfig.sheetName, index),
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: hidden ? "TableStyleLight9" : "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: headers.map((header) => ({ name: header })),
    rows: normalized.map((row) => headers.map((header) => row?.[header] ?? "")),
  });

  headers.forEach((header, colIndex) => {
    const maxContent = normalized.reduce((max, row) => {
      const value = row?.[header];
      return Math.max(max, String(value ?? "").length);
    }, header.length);
    worksheet.getColumn(colIndex + 1).width = Math.min(Math.max(maxContent + 2, 12), 45);
  });

  worksheet.getRow(1).height = 22;
  worksheet.getRow(1).eachCell((cell) => {
    styleCell(cell, {
      font: { bold: true, color: { argb: EXCEL_COLORS.white } },
      alignment: { vertical: "middle", horizontal: "center", wrapText: true },
    });
  });
}

function writeWorkbook(workbook, filename) {
  XLSX.writeFile(workbook, filename, { compression: true });
}

async function writeStyledWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildAdministrativeSheets(data, { startDate, endDate, endExclusive }) {
  const clientsById = mapById(data.clients);
  const offersById = mapById(data.offers);
  const poById = mapById([...data.purchaseOrderView, ...data.purchaseOrders]);
  const instrumentsById = mapById(data.instruments);
  const techniciansById = mapById(data.technicians);
  const serviceOrdersById = mapById(data.serviceOrders);
  const serviceOrdersByInstrumentId = groupBy(data.serviceOrders, "instrument_id");
  const serviceOrdersByPoId = groupBy(data.serviceOrders, "po_id");
  const offerServicesByOfferId = groupBy(data.offerServices, "offer_id");
  const remitoItemsByRemitoId = groupBy(data.remitoItems, "remito_id");

  const getClient = (row = {}) =>
    firstNonEmpty(
      row.client_name,
      clientsById.get(row.client_id)?.name,
      clientsById.get(offersById.get(row.offer_id)?.client_id)?.name,
      clientsById.get(poById.get(row.po_id)?.client_id)?.name,
      clientsById.get(instrumentsById.get(row.instrument_id)?.client_id)?.name
    );

  const getOfferCode = (offerId) => {
    const offer = offersById.get(offerId);
    return firstNonEmpty(offer?.code, offer?.base_number);
  };

  const getPoCode = (poId) => {
    const po = poById.get(poId);
    return firstNonEmpty(po?.code, po?.base_number);
  };

  const getInstrumentLabel = (instrumentId, row = {}) => {
    const instrument = instrumentsById.get(instrumentId);
    return firstNonEmpty(
      row.instrument_tag,
      row.instr_tag,
      instrument?.tag,
      row.instrument_serial,
      row.instr_serial,
      instrument?.serial,
      instrument?.lab_id
    );
  };

  const addMovement = (rows, date, type, row = {}) => {
    if (!isWithinRange(date, startDate, endExclusive)) return;
    rows.push({
      Fecha: formatDateTime(date),
      Tipo: type,
      Cliente: getClient(row),
      Oferta: firstNonEmpty(row.offer_code, getOfferCode(row.offer_id), row.code),
      OC: firstNonEmpty(row.po_code, getPoCode(row.po_id), row.oc_number),
      OS: firstNonEmpty(row.os_code, row.code),
      Instrumento: getInstrumentLabel(row.instrument_id, row),
      Estado: firstNonEmpty(row.status, row.ops_status),
      Accion: row.actionLabel || type,
      Detalle: row.detail || "",
    });
  };

  const movements = [];
  for (const offer of data.offers) {
    addMovement(movements, offer.created_at, "Oferta", {
      ...offer,
      actionLabel: "Oferta creada",
      detail: firstNonEmpty(offer.commercial_condition, offer.notes),
    });
    if (offer.updated_at && offer.updated_at !== offer.created_at) {
      addMovement(movements, offer.updated_at, "Oferta", {
        ...offer,
        actionLabel: "Oferta modificada",
        detail: firstNonEmpty(offer.commercial_condition, offer.notes),
      });
    }
  }
  for (const audit of data.offerAudit) {
    addMovement(movements, audit.changed_at, "Oferta", {
      ...audit,
      client_id: offersById.get(audit.offer_id)?.client_id,
      offer_code: getOfferCode(audit.offer_id),
      actionLabel: `Historial: ${audit.action || "cambio"}`,
      detail: audit.revision != null ? `Revision ${audit.revision}` : "",
    });
  }
  for (const intake of data.intakes) {
    addMovement(movements, intake.created_at, "Ingreso", {
      ...intake,
      actionLabel: "Ingreso provisorio creado",
      detail: intake.code || intake.number || "",
    });
  }
  for (const instrument of data.instruments) {
    addMovement(movements, instrument.reception_date || instrument.created_at, "Instrumento", {
      ...instrument,
      instrument_id: instrument.id,
      actionLabel: "Instrumento ingresado",
      detail: [instrument.brand, instrument.model, instrument.serial].filter(Boolean).join(" "),
    });
    addMovement(movements, instrument.departure_date, "Instrumento", {
      ...instrument,
      instrument_id: instrument.id,
      actionLabel: "Instrumento con salida registrada",
      detail: [instrument.brand, instrument.model, instrument.serial].filter(Boolean).join(" "),
    });
  }
  for (const os of data.serviceOrders) {
    addMovement(movements, os.created_at || os.updated_at, "Orden de servicio", {
      ...os,
      actionLabel: "OS creada o actualizada",
      detail: getInstrumentLabel(os.instrument_id),
    });
  }
  for (const remito of data.remitos) {
    addMovement(movements, remito.date_out || remito.created_at || remito.updated_at, "Remito", {
      ...remito,
      actionLabel: "Remito generado o actualizado",
      detail: firstNonEmpty(remito.official_number, remito.code, remito.pdf_url),
    });
  }
  for (const invoice of data.billingInvoices) {
    addMovement(movements, invoice.requested_at || invoice.created_at, "Facturacion", {
      ...invoice,
      client_id: poById.get(invoice.po_id)?.client_id,
      offer_id: poById.get(invoice.po_id)?.offer_id,
      actionLabel: "Factura solicitada",
      detail: firstNonEmpty(invoice.invoice_number, invoice.note),
    });
    addMovement(movements, invoice.issued_at, "Facturacion", {
      ...invoice,
      client_id: poById.get(invoice.po_id)?.client_id,
      offer_id: poById.get(invoice.po_id)?.offer_id,
      actionLabel: "Factura emitida",
      detail: firstNonEmpty(invoice.invoice_number, invoice.note),
    });
  }
  movements.sort((a, b) => String(a.Fecha).localeCompare(String(b.Fecha)));

  const clientsAndOffers = data.offers.map((offer) => {
    const client = clientsById.get(offer.client_id);
    const linkedPos = data.purchaseOrderView.filter((po) => po.offer_id === offer.id);
    const serviceRows = offerServicesByOfferId.get(offer.id) || [];
    return {
      Cliente: firstNonEmpty(client?.name, getClient(offer)),
      "Codigo cliente": clientCode(client),
      CUIT: firstNonEmpty(client?.cuit, client?.tax_id),
      Oferta: firstNonEmpty(offer.code, offer.base_number),
      Revision: offer.revision ?? "",
      Estado: offer.status || "",
      "Condicion comercial": offer.commercial_condition || "",
      Moneda: offer.price_currency || "",
      Subtotal: offer.subtotal ?? "",
      Total: offer.total ?? "",
      "Items oferta": serviceRows.length,
      "OC asociadas": joinUnique(linkedPos.map((po) => firstNonEmpty(po.code, po.base_number))),
      "Instrumentos en OC": linkedPos.reduce((sum, po) => sum + Number(po.instrument_count || 0), 0),
      "Creada": formatDate(offer.created_at),
      "Actualizada": formatDate(offer.updated_at),
    };
  });

  const instrumentsAndOrders = uniqueRows([
    ...data.serviceOrdersEnriched.map((row) => ({
      id: firstNonEmpty(row.service_order_id, row.id),
      Cliente: getClient(row),
      OC: firstNonEmpty(row.po_code, getPoCode(row.po_id)),
      OS: firstNonEmpty(row.code, row.os_code, row.service_order_code),
      "Estado OS": firstNonEmpty(row.status, row.ops_status),
      Instrumento: getInstrumentLabel(row.instrument_id, row),
      "ID Lab": firstNonEmpty(row.lab_id, row.instr_lab_id),
      Marca: firstNonEmpty(row.brand, row.instr_brand),
      Modelo: firstNonEmpty(row.model, row.instr_model),
      Serie: firstNonEmpty(row.serial, row.instrument_serial, row.instr_serial),
      Tecnico: firstNonEmpty(row.technician_name, techniciansById.get(row.technician_id)?.full_name),
      "Fecha ingreso": formatDate(instrumentsById.get(row.instrument_id)?.reception_date),
      "Fecha salida": formatDate(instrumentsById.get(row.instrument_id)?.departure_date),
    })),
    ...data.instruments.map((instrument) => {
      const relatedOs = serviceOrdersByInstrumentId.get(instrument.id) || [];
      return {
        id: instrument.id,
        Cliente: getClient(instrument),
        OC: joinUnique(relatedOs.map((os) => getPoCode(os.po_id))),
        OS: joinUnique(relatedOs.map((os) => firstNonEmpty(os.code, os.base_number))),
        "Estado OS": joinUnique(relatedOs.map((os) => os.status)),
        Instrumento: getInstrumentLabel(instrument.id),
        "ID Lab": instrument.lab_id || "",
        Marca: instrument.brand || "",
        Modelo: instrument.model || "",
        Serie: instrument.serial || "",
        Tecnico: joinUnique(relatedOs.map((os) => techniciansById.get(os.technician_id)?.full_name)),
        "Fecha ingreso": formatDate(instrument.reception_date || instrument.created_at),
        "Fecha salida": formatDate(instrument.departure_date),
      };
    }),
  ]);

  const remitosAndBilling = [
    ...data.remitoSearch.map((remito) => ({
      Tipo: "Remito",
      Cliente: remito.client_name || getClient(remito),
      CUIT: remito.client_cuit || "",
      OC: "",
      OS: remito.os_code || "",
      Numero: firstNonEmpty(remito.official_number, remito.remito_code),
      Fecha: formatDate(remito.date_out),
      Estado: remito.status || "",
      PDF: remito.pdf_url || "",
      Total: "",
      Observacion: remito.cost_center ? `Centro de costo: ${remito.cost_center}` : "",
    })),
    ...data.remitos.map((remito) => {
      const items = remitoItemsByRemitoId.get(remito.id) || [];
      return {
        Tipo: "Remito",
        Cliente: getClient(remito),
        CUIT: "",
        OC: getPoCode(serviceOrdersById.get(remito.os_id)?.po_id),
        OS: firstNonEmpty(serviceOrdersById.get(remito.os_id)?.code, remito.os_id),
        Numero: firstNonEmpty(remito.official_number, remito.code),
        Fecha: formatDate(remito.date_out || remito.created_at),
        Estado: remito.status || "",
        PDF: remito.pdf_url || "",
        Total: "",
        Observacion: `${items.length} item(s)`,
      };
    }),
    ...data.billingView.map((invoice) => ({
      Tipo: "Factura",
      Cliente: invoice.client_name || getClient(invoice),
      CUIT: invoice.client_cuit || "",
      OC: firstNonEmpty(invoice.oc_number, invoice.po_base_number),
      OS: invoice.os_id || "",
      Numero: invoice.invoice_number || "",
      Fecha: formatDate(invoice.issued_at || invoice.requested_at || invoice.created_at),
      Estado: invoice.status || "",
      PDF: "",
      Total: invoice.total_amount ?? "",
      Observacion: invoice.note || "",
    })),
  ];

  const offerStatus = countBy(data.offers, "status");
  const osStatus = countBy(data.serviceOrders, "status");
  const remitoStatus = countBy(data.remitos, "status");
  const invoiceStatus = countBy(data.billingInvoices, "status");
  const summary = {
    periodLabel: `${formatDate(startDate)} al ${formatDate(endDate)}`,
    generatedAt: new Date().toLocaleString("es-AR"),
    kpis: [
      ["Movimientos visibles", movements.length, "Bitacora principal de la semana"],
      ["Ofertas incluidas", data.offers.length, "Creadas, modificadas o relacionadas con el periodo"],
      ["Clientes incluidos", data.clients.length, "Clientes nuevos o vinculados a movimientos"],
      ["Ingresos provisorios", data.intakes.length, "Ingresos registrados antes de vincular a OC/OS"],
      ["Instrumentos incluidos", data.instruments.length, "Ingresados, salidos o vinculados al periodo"],
      ["Ordenes de servicio", data.serviceOrders.length, "Trabajos operativos e instrumentos asociados"],
      ["Remitos incluidos", data.remitos.length, "Remitos generados o vinculados a OS del periodo"],
      ["Facturas incluidas", data.billingInvoices.length, "Solicitudes o emisiones de factura vinculadas"],
    ],
    actionItems: makeActionRows({ offerStatus, osStatus, remitoStatus, invoiceStatus }),
    offerStatus: statusRows(offerStatus, {
      draft: "Borrador pendiente de envio",
      sent: "Oferta enviada al cliente",
      accepted: "Oferta aceptada",
      billing: "Lista o enviada a facturacion",
      closed: "Oferta cerrada",
      rejected: "Oferta rechazada",
      "Sin dato": "Sin estado cargado",
    }),
    serviceOrderStatus: statusRows(osStatus, {
      delivered_to_lab: "Instrumento entregado a laboratorio",
      calibration_finished: "Calibracion finalizada",
      certificates_finished_controlled: "Certificados finalizados y controlados",
      uploaded_for_remito: "Listo para remitar",
      remito_generated: "Remito generado",
      client_notified: "Cliente notificado",
      pending: "Pendiente",
      in_progress: "En progreso",
      completed: "Completada",
      "Sin dato": "Sin estado cargado",
    }),
    remitoStatus: statusRows(remitoStatus, {
      borrador: "Remito preparado pero no finalizado",
      generado: "Remito generado",
      emitido: "Remito emitido",
      anulado: "Remito anulado",
      "Sin dato": "Sin estado cargado",
    }),
    guide: [
      ["Movimientos", "Bitacora unica de eventos", "Empezar por esta hoja para entender que paso en la semana"],
      ["Clientes y ofertas", "Vista comercial por cliente", "Revisar ofertas, estados, importes y OC asociadas"],
      ["Instrumentos y OS", "Vista operativa", "Consultar instrumentos, OS, tecnico, estado, ingreso y salida"],
      ["Remitos y factura", "Vista administrativa final", "Controlar remitos, PDFs y facturacion"],
      ["Datos - ...", "Hojas ocultas de respaldo", "Usarlas solo para auditoria o soporte tecnico"],
    ],
    workflow: [
      ["1", "Resumen semanal", "Mirar indicadores y pendientes administrativos"],
      ["2", "Movimientos", "Ubicar los eventos principales de la semana"],
      ["3", "Clientes y ofertas", "Confirmar ofertas, OC y estado comercial"],
      ["4", "Instrumentos y OS", "Controlar trabajos, estados tecnicos e instrumentos"],
      ["5", "Remitos y factura", "Cerrar remitos, PDFs y facturacion pendiente"],
    ],
  };

  return {
    summary,
    movements,
    clientsAndOffers,
    instrumentsAndOrders,
    remitosAndBilling,
  };
}

function buildFullBackupData(datasets) {
  const data = FULL_BACKUP_SHEETS.reduce((acc, item) => {
    const dataset = datasets.find((entry) => entry.item.key === item.key);
    acc[item.key] = dataset?.error ? [] : dataset?.rows || [];
    return acc;
  }, {});
  const endDate = toDateInputValue(new Date());
  const endExclusive = getEndExclusive(endDate);

  Object.assign(
    data,
    buildAdministrativeSheets(data, {
      startDate: FULL_BACKUP_START_DATE,
      endDate,
      endExclusive,
    })
  );

  data.summary = {
    ...data.summary,
    periodLabel: `Backup completo desde ${formatDate(FULL_BACKUP_START_DATE)} al ${formatDate(endDate)}`,
    kpis: data.summary.kpis.map(([label, count, detail]) => [
      label,
      count,
      String(detail).replace("de la semana", "del backup completo"),
    ]),
    guide: data.summary.guide.map(([sheet, purpose, recommendation]) => [
      sheet,
      purpose,
      String(recommendation).replace("que paso en la semana", "que paso desde el inicio del sistema"),
    ]),
    workflow: data.summary.workflow.map(([step, sheet, review]) => [
      step,
      sheet === "Resumen semanal" ? "Resumen completo" : sheet,
      review,
    ]),
  };

  return data;
}

async function buildWeeklyReport({ startDate, endDate }) {
  const endExclusive = getEndExclusive(endDate);

  const [
    offers,
    offerAudit,
    clients,
    purchaseOrders,
    purchaseOrderView,
    intakes,
    intakeItemsByDate,
    instrumentsByDate,
    serviceOrdersByDate,
    remitosByDate,
    remitoSearch,
    billingInvoicesByDate,
    billingView,
  ] = await Promise.all([
    fetchRowsByDateFields({
      table: "offers",
      dateFields: ["created_at", "updated_at", "billing_requested_date", "invoiced_date"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "offer_audit",
      dateFields: ["changed_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "clients",
      dateFields: ["created_at", "updated_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "purchase_orders",
      dateFields: ["created_at", "updated_at", "ops_due_date"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "v_purchase_orders",
      dateFields: ["created_at", "ops_due_date"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "intakes",
      dateFields: ["created_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "intake_items",
      dateFields: ["created_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "instruments",
      dateFields: ["created_at", "reception_date", "departure_date"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "service_orders",
      dateFields: ["created_at", "updated_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "remitos",
      dateFields: ["created_at", "updated_at", "date_out"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "v_remitos_search",
      dateFields: ["date_out"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "billing_invoices",
      dateFields: ["created_at", "updated_at", "requested_at", "issued_at"],
      startDate,
      endExclusive,
    }),
    fetchRowsByDateFields({
      table: "v_billing_invoices_list",
      dateFields: ["created_at", "requested_at", "issued_at"],
      startDate,
      endExclusive,
    }),
  ]);

  const offerIds = Array.from(
    new Set([
      ...valuesFrom(offers, "id"),
      ...valuesFrom(offerAudit, "offer_id"),
      ...valuesFrom(purchaseOrders, "offer_id"),
      ...valuesFrom(purchaseOrderView, "offer_id"),
    ])
  );
  const poIds = Array.from(
    new Set([
      ...valuesFrom(purchaseOrders, "id"),
      ...valuesFrom(purchaseOrderView, "id"),
      ...valuesFrom(billingInvoicesByDate, "po_id"),
      ...valuesFrom(billingView, "po_id"),
    ])
  );
  const intakeIds = valuesFrom(intakes, "id");

  const [offerServices, offerServicesAudit, intakeItemsByIntake] = await Promise.all([
    fetchRowsByIds({ table: "offer_services", column: "offer_id", ids: offerIds }),
    fetchRowsByIds({ table: "offer_services_audit", column: "offer_id", ids: offerIds }),
    fetchRowsByIds({ table: "intake_items", column: "intake_id", ids: intakeIds }),
  ]);

  const intakeItems = uniqueRows([...intakeItemsByDate, ...intakeItemsByIntake]);
  const instrumentIdsFromIntakes = valuesFrom(intakeItems, "instrument_id");
  const instrumentsByIntakeItems = await fetchRowsByIds({
    table: "instruments",
    column: "id",
    ids: instrumentIdsFromIntakes,
  });
  const instruments = uniqueRows([...instrumentsByDate, ...instrumentsByIntakeItems]);
  const instrumentIds = valuesFrom(instruments, "id");

  const [serviceOrdersByInstruments, serviceOrdersByPo] = await Promise.all([
    fetchRowsByIds({ table: "service_orders", column: "instrument_id", ids: instrumentIds }),
    fetchRowsByIds({ table: "service_orders", column: "po_id", ids: poIds }),
  ]);
  const serviceOrders = uniqueRows([
    ...serviceOrdersByDate,
    ...serviceOrdersByInstruments,
    ...serviceOrdersByPo,
  ]);
  const osIds = valuesFrom(serviceOrders, "id");

  const [serviceOrdersEnrichedByOs, serviceOrdersEnrichedByPo, servicePrices, servicePriceExtras] =
    await Promise.all([
      fetchRowsByIds({
        table: "v_service_orders_enriched",
        column: "service_order_id",
        ids: osIds,
      }),
      fetchRowsByIds({ table: "v_service_orders_enriched", column: "po_id", ids: poIds }),
      fetchRowsByIds({ table: "service_prices", column: "os_id", ids: osIds }),
      fetchRowsByIds({ table: "service_price_extras", column: "os_id", ids: osIds }),
    ]);
  const serviceOrdersEnriched = uniqueRows([
    ...serviceOrdersEnrichedByOs,
    ...serviceOrdersEnrichedByPo,
  ]);

  const remitosByOs = await fetchRowsByIds({
    table: "remitos",
    column: "os_id",
    ids: osIds,
    orderBy: "date_out",
  });
  const remitos = uniqueRows([...remitosByDate, ...remitosByOs]);
  const remitoIds = Array.from(
    new Set([...valuesFrom(remitos, "id"), ...valuesFrom(remitoSearch, "remito_id")])
  );
  const remitoItems = await fetchRowsByIds({
    table: "remito_items",
    column: "remito_id",
    ids: remitoIds,
  });

  const billingInvoicesByPo = await fetchRowsByIds({
    table: "billing_invoices",
    column: "po_id",
    ids: poIds,
  });
  const billingInvoices = uniqueRows([...billingInvoicesByDate, ...billingInvoicesByPo]);
  const invoiceIds = Array.from(
    new Set([...valuesFrom(billingInvoices, "id"), ...valuesFrom(billingView, "invoice_id")])
  );
  const billingInvoiceItems = await fetchRowsByIds({
    table: "billing_invoice_items",
    column: "invoice_id",
    ids: invoiceIds,
  });

  const referencedClientIds = Array.from(
    new Set([
      ...valuesFrom(offers, "client_id"),
      ...valuesFrom(clients, "id"),
      ...valuesFrom(purchaseOrders, "client_id"),
      ...valuesFrom(purchaseOrderView, "client_id"),
      ...valuesFrom(intakes, "client_id"),
      ...valuesFrom(instruments, "client_id"),
      ...valuesFrom(remitos, "client_id"),
      ...valuesFrom(remitoSearch, "client_id"),
    ])
  );
  const referencedClients = await fetchRowsByIds({
    table: "clients",
    column: "id",
    ids: referencedClientIds,
  });

  const technicianIds = Array.from(
    new Set([
      ...valuesFrom(serviceOrders, "technician_id"),
      ...valuesFrom(serviceOrders, "assigned_to"),
      ...valuesFrom(purchaseOrderView, "assigned_to"),
      ...valuesFrom(purchaseOrders, "assigned_to"),
    ])
  );
  const technicians = await fetchRowsByIds({
    table: "technicians",
    column: "id",
    ids: technicianIds,
  });

  const data = {
    offers,
    offerServices,
    offerAudit,
    offerServicesAudit,
    clients: uniqueRows([...clients, ...referencedClients]),
    purchaseOrders,
    purchaseOrderView,
    intakes,
    intakeItems,
    instruments,
    serviceOrders,
    serviceOrdersEnriched,
    servicePrices,
    servicePriceExtras,
    remitos,
    remitoItems,
    remitoSearch,
    billingInvoices,
    billingInvoiceItems,
    billingView,
    technicians,
  };

  Object.assign(data, buildAdministrativeSheets(data, { startDate, endDate, endExclusive }));

  return data;
}

export default function AdminExportsPage() {
  const initialWeeklyRange = useMemo(() => getPreviousWeekRange(), []);
  const [loadingKey, setLoadingKey] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState("");
  const [weeklyStartDate, setWeeklyStartDate] = useState(initialWeeklyRange.startDate);
  const [weeklyEndDate, setWeeklyEndDate] = useState(initialWeeklyRange.endDate);

  const isBusy = !!loadingKey;

  const totalLabel = useMemo(() => {
    if (!lastResult?.counts) return "";
    if (lastResult.type === "weekly") {
      return `Periodo: ${lastResult.startDate} a ${lastResult.endDate}. Hojas visibles: ${WEEKLY_ADMIN_SHEETS.length}`;
    }
    if (lastResult.type === "backup") {
      const totalRecords = Object.values(lastResult.counts).reduce(
        (total, count) => total + (Number(count) || 0),
        0
      );
      const warningText = lastResult.errorCount
        ? `. Avisos: ${lastResult.errorCount}`
        : "";
      return `Tablas/vistas: ${lastResult.tableCount}. Registros: ${totalRecords}${warningText}`;
    }
    return EXPORTS.map((item) => `${item.label}: ${lastResult.counts[item.key] ?? 0}`).join(" | ");
  }, [lastResult]);

  const downloadSingle = async (item) => {
    setLoadingKey(item.key);
    setError("");
    setLastResult(null);

    try {
      const { default: ExcelJS } = await import("exceljs");
      const rows = await fetchAllRows(item);
      const clientIds = item.key === "clients" ? [] : valuesFrom(rows, "client_id");
      const clients = await fetchRowsByIds({
        table: "clients",
        column: "id",
        ids: clientIds,
      });
      const friendlyRows = buildFriendlyRows(item, rows, { clients });
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Calins App";
      workbook.created = new Date();
      workbook.modified = new Date();

      appendIndividualSummarySheet(workbook, item, rows, friendlyRows);
      appendStyledTableSheet(workbook, item, friendlyRows, { index: 0 });
      appendStyledTableSheet(
        workbook,
        { ...item, sheetName: `Datos - ${item.sheetName}` },
        rows,
        { hidden: true, index: 1 }
      );

      await writeStyledWorkbook(workbook, `${item.table}_${timestampForFile()}.xlsx`);
      setLastResult({ type: "single", label: item.label, counts: { [item.key]: rows.length } });
    } catch (err) {
      console.error("[AdminExports] downloadSingle", err);
      setError(err?.message || `No se pudo exportar ${item.label}.`);
    } finally {
      setLoadingKey("");
    }
  };

  const downloadFullBackup = async () => {
    setLoadingKey("backup");
    setError("");
    setLastResult(null);

    try {
      const { default: ExcelJS } = await import("exceljs");
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Calins App";
      workbook.created = new Date();
      workbook.modified = new Date();
      const counts = {};
      const datasets = [];

      for (const item of FULL_BACKUP_SHEETS) {
        const dataset = await fetchBackupDataset(item);
        datasets.push(dataset);
        counts[item.key] = dataset.error ? 0 : dataset.rows.length;
      }

      const data = buildFullBackupData(datasets);
      let sheetIndex = 0;

      for (const sheet of WEEKLY_ADMIN_SHEETS) {
        const rows = data[sheet.key] || [];
        counts[sheet.key] = Array.isArray(rows) ? rows.length : 1;
        if (sheet.key === "summary") {
          appendStyledWeeklySummarySheet(workbook, rows, {
            title: "Backup completo de movimientos",
            sheetName: "Resumen completo",
          });
        } else {
          appendStyledTableSheet(workbook, sheet, rows, { index: sheetIndex });
        }
        sheetIndex += 1;
      }

      appendBackupIndexSheet(workbook, datasets);
      sheetIndex += 1;

      datasets.forEach((dataset) => {
        appendStyledTableSheet(workbook, dataset.item, dataset.rows, {
          hidden: true,
          index: sheetIndex,
        });
        sheetIndex += 1;
      });

      await writeStyledWorkbook(workbook, `backup_completo_calins_${timestampForFile()}.xlsx`);
      setLastResult({
        type: "backup",
        label: "Backup completo",
        counts,
        tableCount: datasets.length,
        errorCount: datasets.filter((dataset) => dataset.error).length,
      });
    } catch (err) {
      console.error("[AdminExports] downloadFullBackup", err);
      setError(err?.message || "No se pudo generar el backup completo.");
    } finally {
      setLoadingKey("");
    }
  };

  const applyPreviousWeek = () => {
    const range = getPreviousWeekRange();
    setWeeklyStartDate(range.startDate);
    setWeeklyEndDate(range.endDate);
  };

  const downloadWeeklyReport = async () => {
    if (!weeklyStartDate || !weeklyEndDate) {
      setError("Indica fecha desde y hasta para generar el reporte semanal.");
      return;
    }
    if (weeklyStartDate > weeklyEndDate) {
      setError("La fecha desde no puede ser posterior a la fecha hasta.");
      return;
    }

    setLoadingKey("weekly");
    setError("");
    setLastResult(null);

    try {
      const { default: ExcelJS } = await import("exceljs");
      const data = await buildWeeklyReport({
        startDate: weeklyStartDate,
        endDate: weeklyEndDate,
      });
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Calins App";
      workbook.created = new Date();
      workbook.modified = new Date();
      const counts = {};
      let sheetIndex = 0;

      for (const sheet of WEEKLY_ADMIN_SHEETS) {
        const rows = data[sheet.key] || [];
        counts[sheet.key] = Array.isArray(rows) ? rows.length : 1;
        if (sheet.key === "summary") {
          appendStyledWeeklySummarySheet(workbook, rows);
        } else {
          appendStyledTableSheet(workbook, sheet, rows, { index: sheetIndex });
        }
        sheetIndex += 1;
      }
      for (const sheet of WEEKLY_RAW_SHEETS) {
        const rows = data[sheet.key] || [];
        counts[sheet.key] = rows.length;
        appendStyledTableSheet(workbook, sheet, rows, { hidden: true, index: sheetIndex });
        sheetIndex += 1;
      }

      await writeStyledWorkbook(
        workbook,
        `movimientos_semana_${weeklyStartDate}_a_${weeklyEndDate}.xlsx`
      );
      setLastResult({
        type: "weekly",
        label: "Reporte semanal de movimientos",
        startDate: weeklyStartDate,
        endDate: weeklyEndDate,
        counts,
      });
    } catch (err) {
      console.error("[AdminExports] downloadWeeklyReport", err);
      setError(err?.message || "No se pudo generar el reporte semanal.");
    } finally {
      setLoadingKey("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      <header className="flex flex-col gap-4 py-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Exportaciones</h1>
          <p className="text-sm text-muted-foreground">
            Descarga en Excel reportes semanales, backups completos y tablas principales.
          </p>
        </div>
      </header>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      {lastResult ? (
        <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          Excel generado: {lastResult.label}. {totalLabel}
        </div>
      ) : null}

      <section className="mb-6 rounded-xl border border-sky-500/30 bg-sky-500/10 p-5 text-sky-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-sky-200" />
              <h2 className="text-lg font-semibold">Backup completo</h2>
            </div>
            <p className="mt-1 text-sm text-sky-50/80">
              Descarga un Excel con todas las tablas y vistas operativas importantes,
              sin filtro de fechas. Incluye clientes, ofertas, OC, ingresos,
              instrumentos, OS, remitos y facturacion.
            </p>
          </div>

          <button
            type="button"
            onClick={downloadFullBackup}
            disabled={isBusy}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-400/50 bg-emerald-500/15 px-4 py-2 text-sm font-medium text-emerald-50 hover:bg-emerald-500/25 disabled:cursor-wait disabled:opacity-60"
          >
            {loadingKey === "backup" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            {loadingKey === "backup" ? "Generando..." : "Descargar Backup completo"}
          </button>
        </div>
      </section>

      <section className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 text-amber-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-amber-200" />
              <h2 className="text-lg font-semibold">Reporte semanal de movimientos</h2>
            </div>
            <p className="mt-1 text-sm text-amber-50/80">
              Genera un Excel de contingencia con ofertas, clientes, ingresos,
              instrumentos, ordenes de servicio, remitos y facturacion del periodo.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
            <label className="text-xs font-medium text-amber-50/80">
              Desde
              <input
                type="date"
                value={weeklyStartDate}
                onChange={(event) => setWeeklyStartDate(event.target.value)}
                className="mt-1 w-full rounded-md border border-amber-400/30 bg-black/20 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-200"
              />
            </label>
            <label className="text-xs font-medium text-amber-50/80">
              Hasta
              <input
                type="date"
                value={weeklyEndDate}
                onChange={(event) => setWeeklyEndDate(event.target.value)}
                className="mt-1 w-full rounded-md border border-amber-400/30 bg-black/20 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-200"
              />
            </label>
            <button
              type="button"
              onClick={applyPreviousWeek}
              disabled={isBusy}
              className="inline-flex items-center justify-center rounded-md border border-amber-400/40 px-3 py-2 text-sm font-medium text-amber-50 hover:bg-amber-400/10 disabled:cursor-wait disabled:opacity-60"
            >
              Semana anterior
            </button>
            <button
              type="button"
              onClick={downloadWeeklyReport}
              disabled={isBusy}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-400/50 bg-emerald-500/15 px-4 py-2 text-sm font-medium text-emerald-50 hover:bg-emerald-500/25 disabled:cursor-wait disabled:opacity-60"
            >
              {loadingKey === "weekly" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              {loadingKey === "weekly" ? "Generando..." : "Generar reporte"}
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {EXPORTS.map((item) => {
          const itemLoading = loadingKey === item.key;
          return (
            <article
              key={item.key}
              className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{item.label}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                </div>
                <FileSpreadsheet className="h-5 w-5 shrink-0 text-emerald-300" />
              </div>

              <button
                type="button"
                onClick={() => downloadSingle(item)}
                disabled={isBusy}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-sky-500/50 px-4 py-2 text-sm font-medium text-sky-100 transition-colors hover:bg-sky-500/10 disabled:cursor-wait disabled:opacity-60"
              >
                {itemLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {itemLoading ? "Preparando..." : `Descargar ${item.label}`}
              </button>
            </article>
          );
        })}
      </section>

      <div className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <div className="flex items-start gap-3">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <p>
            La descarga trae todos los registros visibles para tu usuario segun las reglas de
            seguridad de Supabase. Si una tabla supera 1000 filas, se descarga por bloques
            automaticamente.
          </p>
        </div>
      </div>
    </div>
  );
}
